/**
 * Figure numbering.
 *
 * Numbers are assigned by scanning the source before render rather than by a
 * counter incremented during it — React may render the same subtree twice
 * (StrictMode, concurrent re-entry) and a stateful counter would renumber the
 * figures underneath the reader.
 */

/**
 * Both figure fences, in one sequence. A reader following "as Figure 3 shows"
 * does not care whether it was drawn from a spec or animated in a sandbox, and
 * two independent counters would give them two Figure 3s.
 */
const FIGURE_FENCE =
  /^([ \t]*)(`{3,}|~{3,})[ \t]*(?:plot|anim)[^\n]*\n([\s\S]*?)(?:\n[ \t]*\2|$)/gm;

export interface FigureIndex {
  /** 1-based figure number for a block's source text, if it is one. */
  readonly numberOf: (source: string) => number | undefined;
  readonly count: number;
}

export function indexFigures(markdown: string): FigureIndex {
  const numbers = new Map<string, number>();
  let count = 0;

  FIGURE_FENCE.lastIndex = 0;
  let match = FIGURE_FENCE.exec(markdown);

  while (match !== null) {
    const body = normalize(match[3] ?? "");
    if (!numbers.has(body)) {
      count += 1;
      numbers.set(body, count);
    }
    match = FIGURE_FENCE.exec(markdown);
  }

  return {
    count,
    numberOf: (source) => numbers.get(normalize(source)),
  };
}

/**
 * Indentation and trailing whitespace differ between the raw source and what
 * the markdown parser hands back, so compare on a stripped form.
 */
function normalize(source: string): string {
  return source
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .trim();
}

/**
 * True when the last fence in the text is still open — i.e. the tutor is
 * mid-figure and the block should show a placeholder rather than an error.
 */
export function hasOpenFence(markdown: string): boolean {
  const fences = markdown.match(/^[ \t]*(`{3,}|~{3,})/gm);
  return fences !== null && fences.length % 2 === 1;
}
